import countries from '@/config/countries.json'
import type { ClientData } from '@/models/client.js'
import { formatDateToRelativeTime } from '@/utils/date/conversion.js'
import { useCurrentLocale } from '@/composables/i18next.js'
import { getCurrentTimestamp } from '@/utils/date/getter.js'

const getCurrentLocaleValue = () => useCurrentLocale().value

const relativeTimeOptions: Intl.RelativeTimeFormatOptions = { numeric: 'auto' }
const relativeTimeFormatter = computed(
  () =>
    new Intl.RelativeTimeFormat(getCurrentLocaleValue(), relativeTimeOptions),
)

const dateOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}
const dateFormatter = computed(
  () => new Intl.DateTimeFormat(getCurrentLocaleValue(), dateOptions),
)

const timeOptions: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
}
const timeFormatter = computed(
  () => new Intl.DateTimeFormat(getCurrentLocaleValue(), timeOptions),
)

const dateTimeOptions: Intl.DateTimeFormatOptions = {
  ...dateOptions,
  ...timeOptions,
}
const dateTimeFormatter = computed(
  () => new Intl.DateTimeFormat(getCurrentLocaleValue(), dateTimeOptions),
)

export function useRelativeTimeFormat() {
  return {
    format: (date: Date) =>
      formatDateToRelativeTime(relativeTimeFormatter.value, date),
  }
}

export function useDateTimeFormat() {
  return {
    date: (date: Date) => dateFormatter.value.format(date),
    time: (date: Date) => timeFormatter.value.format(date),
    dateTime: (date: Date) => dateTimeFormatter.value.format(date),
  }
}

/**
 * Creates a formatter to consistently translate regions
 */
const regionNameFormatter = computed(
  () => new Intl.DisplayNames([getCurrentLocaleValue()], { type: 'region' }),
)

export function useDisplayNames() {
  const INVALID_COUNTRY_MESSAGE = 'Invalid or unsupported country'

  return {
    regionName: (countryCode: string) =>
      countryCode
        ? (regionNameFormatter.value.of(countryCode) ?? INVALID_COUNTRY_MESSAGE)
        : INVALID_COUNTRY_MESSAGE,
  }
}

/**
 * Module-level cache for timezone formatters, shared across all component instances.
 *
 * Uses a plain Map (not reactive) since this is purely for performance optimization.
 * The Intl.DateTimeFormat constructor is expensive, so we cache formatters per timezone
 * to avoid repeated instantiation. Cache persists across route navigation and component
 * re-renders, only cleared on page refresh.
 *
 * @example Cache behavior:
 * - First call: formatTimezone('Europe/Paris') → Creates & caches formatter
 * - Subsequent calls: formatTimezone('Europe/Paris') → Uses cached formatter
 */
const timezoneFormatters = new Map<string, Intl.DateTimeFormat>()

/**
 * Composable for formatting timezone identifiers to short timezone names.
 *
 * Uses the current app locale to determine timezone abbreviation format.
 * Caches Intl.DateTimeFormat instances per timezone to avoid recreating formatters
 * for repeated calls with the same timezone.
 *
 * @example
 * const { formatTimezone } = useTimezoneFormat()
 * formatTimezone('Europe/Paris')
 * // Output varies by locale:
 * // en-US: "GMT+1" / "GMT+2"
 * // it-IT: "CET" / "CEST"
 * // de-DE: "MEZ" / "MESZ"
 * formatTimezone('America/New_York') // "EST" / "EDT" in most locales
 */
export function useTimezoneFormat() {
  const formatTimezone = (timezone: string) => {
    try {
      const locale = getCurrentLocaleValue()
      const cacheKey = `${locale}::${timezone}`
      let formatter = timezoneFormatters.get(cacheKey)

      // If no formatter for this timezone is in cache, call new Intl.DateTimeFormat
      if (!formatter) {
        formatter = new Intl.DateTimeFormat(locale, {
          timeZone: timezone,
          timeZoneName: 'short',
        })
        timezoneFormatters.set(cacheKey, formatter)
      }

      const parts = formatter.formatToParts(getCurrentTimestamp())
      return (
        parts.find((part) => part.type === 'timeZoneName')?.value || timezone
      )
    } catch {
      return timezone
    }
  }

  return {
    formatTimezone,
  }
}

export type CountriesList = {
  code: ClientData['country']
  name: string
}[]
export function useCountriesList() {
  const countriesList = countries as CountriesList

  return countriesList
    .filter((country) => !!country.code)
    .map((country) => ({
      label: regionNameFormatter.value.of(country.code!) ?? country.code!,
      value: country.code!,
    }))
}
